import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { finalize } from "rxjs/operators";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { ToastrService } from "../../shared/toastr.service";
import {
  ALLOWED_CLIENT_DOCUMENT_MIME_TYPES,
  ClientDocumentAddDialogData,
  ClientDocumentMeta,
} from "../client.interface";
import { ClientService } from "../client.service";

@Component({
  selector: "app-client-document-add-dialog",
  templateUrl: "./client-document-add-dialog.component.html",
  styleUrls: ["./client-document-add-dialog.component.scss"],
  standalone: false,
})
export class ClientDocumentAddDialogComponent implements OnInit {
  file: File | null = null;
  form!: UntypedFormGroup;
  saving = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  readonly allowedMimeTypes = [...ALLOWED_CLIENT_DOCUMENT_MIME_TYPES];
  readonly dropDescription = this.translate.instant(
    "clientDetail.documents.dropzone",
  ) as string;
  readonly wrongTypeMessage = this.translate.instant(
    "clientDetail.documents.wrongType",
  ) as string;

  constructor(
    private readonly dialogRef: MatDialogRef<
      ClientDocumentAddDialogComponent,
      ClientDocumentMeta | undefined
    >,
    @Inject(MAT_DIALOG_DATA) private readonly data: ClientDocumentAddDialogData,
    private readonly clientService: ClientService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.fillInitialForm();
  }

  fillInitialForm = (): void => {
    this.form = new UntypedFormGroup({
      file: new UntypedFormControl("", (control) =>
        Validators.required(control),
      ),
      name: new UntypedFormControl("", (control) =>
        Validators.required(control),
      ),
    });
  };

  onSubmit = (): void => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.saving || this.form.invalid || !this.file) {
      return;
    }
    this.saving = true;
    const name = String(this.form.get("name")?.value ?? "").trim();
    this.clientService
      .uploadDocument(this.data.clientId, this.file, name)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: (document) => {
          this.toastr.success(
            this.translate.instant("clientDetail.documents.uploaded") as string,
          );
          this.dialogRef.close(document);
        },
      });
  };

  fileChange = ({ file }: { file: File }): void => {
    this.file = file;
    this.form.patchValue({ file: file.name });
    if (!this.form.get("name")?.value) {
      this.form.patchValue({ name: file.name.replace(/\.[^.]+$/, "") });
    }
  };

  removeFile = (): void => {
    this.file = null;
    this.form.patchValue({ file: "" });
  };

  cancel = (): void => {
    if (this.saving) {
      return;
    }
    this.dialogRef.close();
  };
}
